import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { MonthPicker } from "../components/ui/MonthPicker";
import { useMonthlyBudget } from "../hooks/budget";
import type { BudgetRow } from "../lib/budget";
import { formatEur } from "../lib/format-kpi";

function currentMonth(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function lastDayOfMonth(ym: string): string {
  const [yStr, mStr] = ym.split("-");
  const y = Number(yStr);
  const m = Number(mStr);
  const d = new Date(Date.UTC(y, m, 0));
  return d.toISOString().slice(0, 10);
}

function sourceBadgeStyle(src: BudgetRow["actualSource"]): {
  bg: string;
  fg: string;
} {
  if (src === "auto") return { bg: "rgba(14, 165, 233, 0.15)", fg: "#38bdf8" };
  if (src === "manuell") return { bg: "var(--border-soft)", fg: "var(--text)" };
  return { bg: "var(--warn-soft)", fg: "var(--warn)" };
}

export function BudgetMonthly() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const month = params.get("month") ?? currentMonth();
  const { data, isLoading } = useMonthlyBudget(month);

  function setMonth(ym: string) {
    const next = new URLSearchParams(params);
    next.set("month", ym);
    setParams(next, { replace: true });
  }

  if (isLoading || !data) {
    return (
      <div className="fv-card p-10 text-center" style={{ color: "var(--muted)" }}>
        …
      </div>
    );
  }

  const bySection: Record<"INCOME" | "EXPENSE" | "TRANSFER", BudgetRow[]> = {
    INCOME: [],
    EXPENSE: [],
    TRANSFER: [],
  };
  for (const row of data.rows) {
    bySection[row.direction].push(row);
  }

  const incomeVar =
    data.totals.actualIncomeCents - data.totals.plannedIncomeCents;
  const expenseVar =
    data.totals.actualExpenseCents - data.totals.plannedExpenseCents;
  const saldoPlan =
    data.totals.plannedIncomeCents - data.totals.plannedExpenseCents;
  const saldoIst =
    data.totals.actualIncomeCents - data.totals.actualExpenseCents;

  return (
    <section className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div
            className="text-xs font-semibold uppercase tracking-wider mb-1"
            style={{ color: "var(--muted)" }}
          >
            {t("nav.group_cashflow")}
          </div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t("budget.title", { defaultValue: "Budget" })}
          </h1>
          <div className="text-sm mt-1" style={{ color: "var(--muted)" }}>
            {t("budget.subtitle", {
              defaultValue: "Plan vs. Ist je Kategorie",
            })}
          </div>
        </div>
        <MonthPicker
          value={month}
          onChange={setMonth}
          aria-label={
            t("budget.month_picker", { defaultValue: "Monat waehlen" }) ??
            undefined
          }
        />
      </div>

      {/* Stats Strip */}
      <div
        className="fv-card grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x"
        style={{ borderColor: "var(--border)" }}
      >
        <SumPlanIst
          label={t("budget.summary.income", { defaultValue: "Einnahmen" })}
          plan={data.totals.plannedIncomeCents}
          ist={data.totals.actualIncomeCents}
          tone="pos"
        />
        <SumPlanIst
          label={t("budget.summary.expense", { defaultValue: "Ausgaben" })}
          plan={data.totals.plannedExpenseCents}
          ist={data.totals.actualExpenseCents}
          tone="neg"
        />
        <SumPlanIst
          label={t("budget.summary.saldo", { defaultValue: "Saldo" })}
          plan={saldoPlan}
          ist={saldoIst}
          tone={saldoIst >= 0 ? "pos" : "neg"}
          emphasis
        />
        <SumPlanIst
          label={t("budget.summary.variance", { defaultValue: "Abweichung" })}
          plan={incomeVar - expenseVar}
          ist={incomeVar - expenseVar}
          tone={incomeVar - expenseVar >= 0 ? "pos" : "neg"}
          singleValue
        />
      </div>

      {/* Sections */}
      {(["INCOME", "EXPENSE", "TRANSFER"] as const).map((dir) => {
        const rows = bySection[dir];
        if (rows.length === 0) return null;
        const sectionTitle =
          dir === "INCOME"
            ? t("budget.section.income", { defaultValue: "Einnahmen" })
            : dir === "EXPENSE"
              ? t("budget.section.expense", { defaultValue: "Ausgaben" })
              : t("budget.section.transfer", { defaultValue: "Umbuchungen" });

        return (
          <div key={dir} className="fv-card overflow-hidden">
            <div
              className="px-5 py-3 flex items-center gap-3 border-b"
              style={{ borderColor: "var(--border)" }}
            >
              <h2 className="text-base font-semibold">{sectionTitle}</h2>
              <span className="text-xs" style={{ color: "var(--muted)" }}>
                {rows.length}{" "}
                {rows.length === 1
                  ? t("budget.category_singular", {
                      defaultValue: "Kategorie",
                    })
                  : t("budget.category_plural", {
                      defaultValue: "Kategorien",
                    })}
              </span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr
                  className="text-left text-xs"
                  style={{
                    background: "var(--border-soft)",
                    color: "var(--muted)",
                  }}
                >
                  <th className="px-3 py-2 font-semibold">
                    {t("budget.column.category", { defaultValue: "Kategorie" })}
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">
                    {t("budget.column.planned", { defaultValue: "Plan" })}
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">
                    {t("budget.column.actual", { defaultValue: "Ist" })}
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">
                    {t("budget.column.variance", {
                      defaultValue: "Abweichung",
                    })}
                  </th>
                  <th className="px-3 py-2 font-semibold">
                    {t("budget.column.source", { defaultValue: "Quelle" })}
                  </th>
                  <th className="px-3 py-2 w-8" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const bad =
                    dir === "EXPENSE"
                      ? Math.abs(row.actualCents) >
                        Math.abs(row.plannedCents) * 1.1
                      : dir === "INCOME"
                        ? row.actualCents < row.plannedCents * 0.9
                        : false;
                  const to = row.categoryId
                    ? `/transactions?categoryId=${row.categoryId}&from=${month}-01&to=${lastDayOfMonth(month)}`
                    : `/transactions?from=${month}-01&to=${lastDayOfMonth(month)}`;
                  const categoryLabel =
                    row.categoryId === null
                      ? t("budget.uncategorized", {
                          defaultValue: "Ohne Kategorie",
                        })
                      : row.categoryName;
                  const sourceLabel =
                    row.actualSource === "auto"
                      ? t("budget.source.auto", {
                          defaultValue: "Auto-Import",
                        })
                      : row.actualSource === "manuell"
                        ? t("budget.source.manuell", {
                            defaultValue: "Manuell",
                          })
                        : t("budget.source.mix", {
                            defaultValue: "Gemischt",
                          });
                  const srcStyle = sourceBadgeStyle(row.actualSource);
                  return (
                    <tr
                      key={`${dir}-${row.categoryId ?? "null"}`}
                      className="border-b transition-colors hover:bg-[var(--row-hover)]"
                      style={{
                        borderColor: "var(--border-soft)",
                        background: bad ? "var(--neg-soft)" : "transparent",
                      }}
                    >
                      <td className="px-3 py-2.5">{categoryLabel}</td>
                      <td className="px-3 py-2.5 text-right num">
                        {formatEur(row.plannedCents)}
                      </td>
                      <td className="px-3 py-2.5 text-right num">
                        {formatEur(row.actualCents)}
                      </td>
                      <td
                        className="px-3 py-2.5 text-right num"
                        style={{
                          color: bad ? "var(--neg)" : "var(--muted)",
                          fontWeight: bad ? 600 : 400,
                        }}
                      >
                        {formatEur(row.varianceCents)}
                      </td>
                      <td className="px-3 py-2.5 text-xs">
                        <span
                          className="px-2 py-0.5 rounded-md font-medium"
                          style={{
                            background: srcStyle.bg,
                            color: srcStyle.fg,
                          }}
                        >
                          {sourceLabel}
                        </span>
                        {row.staleAccountIds.length > 0 && (
                          <span
                            className="ml-1"
                            style={{ color: "var(--warn)" }}
                            title={
                              t("budget.source.stale_tooltip", {
                                count: row.staleAccountIds.length,
                                defaultValue:
                                  "{{count}} Konto/Konten ohne aktuellen Import - moeglicherweise unvollstaendig",
                              }) ?? undefined
                            }
                          >
                            ⚠
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <Link
                          to={to}
                          className="text-xs"
                          style={{ color: "var(--muted)" }}
                        >
                          →
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </section>
  );
}

function SumPlanIst({
  label,
  plan,
  ist,
  tone,
  emphasis,
  singleValue,
}: {
  label: string;
  plan: number;
  ist: number;
  tone?: "pos" | "neg";
  emphasis?: boolean;
  singleValue?: boolean;
}) {
  const color =
    tone === "pos" ? "var(--pos)" : tone === "neg" ? "var(--neg)" : "var(--text)";
  const diff = ist - plan;
  return (
    <div className="p-4">
      <div className="fv-kpi-label mb-1">{label}</div>
      <div
        className={`${emphasis ? "text-2xl" : "text-xl"} font-bold tracking-tight num`}
        style={{ color }}
      >
        {formatEur(singleValue ? plan : plan)}
      </div>
      {!singleValue && (
        <div
          className="text-xs mt-1 num"
          style={{ color: "var(--muted)" }}
        >
          {ist === 0 ? (
            <>—</>
          ) : (
            <>
              Ist <b style={{ color: "var(--text)" }}>{formatEur(ist)}</b>{" "}
              <span style={{ color: diff >= 0 ? "var(--pos)" : "var(--neg)" }}>
                · {diff >= 0 ? "+" : ""}
                {formatEur(diff)}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
}
